import React from 'react';
import { Building, Hash, Settings, Mail, Calendar, Send, FileWarning } from 'lucide-react';
import { formatJobRevNumber } from '../utils/projectFormatting';

const ModelBuildingManagerCard = ({ project, onClick, emailTracking, showDiscrepancies, discrepancies, onDiscrepancyClick }) => {
  const formatJobNumber = () => {
    return formatJobRevNumber(project?.jobNumber, project?.rev, "N/A");
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--dark-elevated)',
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        cursor: 'pointer',
        transition: 'all var(--transition-fast)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--gray-500)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--gray-600)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Card Header */}
      <div
        style={{
          marginBottom: 'var(--space-3)',
          paddingBottom: 'var(--space-3)',
          borderBottom: '1px solid var(--gray-700)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)',
            margin: '0 0 var(--space-2) 0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={project.projectName}
        >
          {project.projectName || 'Unnamed Project'}
        </h3>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)',
            backgroundColor: 'var(--gray-700)',
            padding: '4px 10px',
            borderRadius: 'var(--radius-md)',
            width: 'fit-content',
          }}
        >
          <Hash size={14} style={{ color: 'var(--gray-400)' }} />
          <span>{formatJobNumber()}</span>
        </div>
      </div>

      {/* Card Body */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        {project.clientName && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
            }}
          >
            <Building
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={project.clientName}
            >
              {project.clientName}
            </span>
          </div>
        )}

        {project.modelingStatus && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
            }}
          >
            <Settings
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={project.modelingStatus}
            >
              {project.modelingStatus}
            </span>
          </div>
        )}

        {/* Show Quick Deadline email sent date */}
        {emailTracking && emailTracking.quick_deadline_email_sent_at && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
            }}
          >
            <Mail
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: '#f59e0b',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
              }}
              title="Quick Deadline email sent"
            >
              Quick Deadline Email Sent - {formatShortDate(emailTracking.quick_deadline_email_sent_at)}
            </span>
          </div>
        )}

        {/* Show initial email from On Hold dashboard */}
        {project.holdEmailSentDate && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
            }}
          >
            <Send
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--cta-blue)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
              }}
              title="Initial email sent from On Hold dashboard"
            >
              Initial: {formatDate(project.holdEmailSentDate)}
            </span>
          </div>
        )}

        {/* Show recurring email tracking */}
        {emailTracking && (
          <>
            {emailTracking.last_email_sent_date && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-2)',
                }}
              >
                <Mail
                  size={14}
                  style={{
                    flexShrink: 0,
                    marginTop: '2px',
                    color: 'var(--gray-400)',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--gray-300)',
                  }}
                  title="Last recurring email sent"
                >
                  Last: {formatDate(emailTracking.last_email_sent_date)}
                </span>
              </div>
            )}
            {emailTracking.next_reminder_date && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-2)',
                }}
              >
                <Calendar
                  size={14}
                  style={{
                    flexShrink: 0,
                    marginTop: '2px',
                    color: 'var(--gray-400)',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--gray-300)',
                  }}
                  title="Next reminder date"
                >
                  Next: {formatDate(emailTracking.next_reminder_date)}
                </span>
              </div>
            )}
          </>
        )}

        {/* Show discrepancies if in action items mode */}
        {showDiscrepancies && discrepancies && discrepancies.length > 0 && (
          <>
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                padding: '4px 8px',
                marginBottom: 'var(--space-2)',
                background: 'rgba(251, 146, 60, 0.9)',
                color: 'white',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-semibold)',
                textTransform: 'uppercase',
                letterSpacing: '0.025em',
              }}
            >
              <FileWarning size={12} />
              <span>
                {discrepancies.length} discrepanc{discrepancies.length === 1 ? 'y' : 'ies'} for review
              </span>
            </div>
            <div
              style={{
                marginTop: 'var(--space-3)',
                padding: 'var(--space-2)',
                background: 'var(--dark-surface)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--gray-700)',
              }}
            >
              {discrepancies.slice(0, 3).map((discrepancy) => (
                <div
                  key={discrepancy.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onDiscrepancyClick) {
                      onDiscrepancyClick(discrepancy);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 8px',
                    marginBottom: '4px',
                    background: 'var(--dark-bg)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'all var(--transition-fast)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                    e.currentTarget.style.transform = 'translateX(2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--dark-bg)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-50)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <FileWarning size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    {discrepancy.discrepancy_type || discrepancy.type || 'Discrepancy'}
                  </div>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '10px',
                      fontWeight: 'var(--font-semibold)',
                      textTransform: 'uppercase',
                      background: (discrepancy.discrepancy_status || discrepancy.status || '').toLowerCase() === 'draft'
                        ? 'rgba(156, 163, 175, 0.2)'
                        : 'rgba(251, 146, 60, 0.2)',
                      color: (discrepancy.discrepancy_status || discrepancy.status || '').toLowerCase() === 'draft'
                        ? '#6b7280'
                        : '#f97316',
                    }}
                  >
                    {discrepancy.discrepancy_status || discrepancy.status || 'Draft'}
                  </span>
                </div>
              ))}
              {discrepancies.length > 3 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    fontStyle: 'italic',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--gray-400)',
                    padding: '6px 8px',
                  }}
                >
                  +{discrepancies.length - 3} more...
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ModelBuildingManagerCard;